// src/components/AccountReports.js — Profit & Loss, Balance Sheet, Trial Balance, Account QuickReport
// [marker: account-reports v1]
// [marker: fin-reports v1] fiscal-year presets; P&L by month / quarter / vs. previous year
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import api from '../utils/api';
import toast from 'react-hot-toast';
import DataGrid from './DataGrid';
import { orderAccounts, TYPE_LABELS, AccountSelect, useAccounts } from './AccountSelect';

const money = (n) => {
  const v = parseFloat(n || 0);
  return (v < 0 ? '-' : '') + '$' + Math.abs(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};
const ymd = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const fmtDay = (s) => (s ? new Date(s + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '');

// Fiscal year: the month it starts (1 = January), loaded once from the server
let FISCAL_START = 1;
let fiscalPromise = null;
export function loadFiscalStart(force) {
  if (!fiscalPromise || force) {
    fiscalPromise = api.get('/fin-reports/fiscal').then(r => { FISCAL_START = r.data?.startMonth || 1; return FISCAL_START; }).catch(() => FISCAL_START);
  }
  return fiscalPromise;
}
export function setFiscalStartLocal(m) { FISCAL_START = m; fiscalPromise = Promise.resolve(m); }
export function useFiscalStart() {
  const [m, setM] = useState(FISCAL_START);
  useEffect(() => { let on = true; loadFiscalStart().then(v => on && setM(v)); return () => { on = false; }; }, []);
  return m;
}
function fiscalYearOf(d, fsm) { // returns the calendar year the fiscal year containing d starts in
  return d.getMonth() + 1 >= fsm ? d.getFullYear() : d.getFullYear() - 1;
}

export function rangePreset(key) {
  const now = new Date(); const y = now.getFullYear(); const m = now.getMonth();
  const fsm = FISCAL_START; const fy = fiscalYearOf(now, fsm);
  switch (key) {
    case 'this_fy': return { from: ymd(new Date(fy, fsm - 1, 1)), to: ymd(new Date(fy + 1, fsm - 1, 0)) };
    case 'fy_to_date': return { from: ymd(new Date(fy, fsm - 1, 1)), to: ymd(now) };
    case 'last_fy': return { from: ymd(new Date(fy - 1, fsm - 1, 1)), to: ymd(new Date(fy, fsm - 1, 0)) };
    case 'this_year': return { from: `${y}-01-01`, to: `${y}-12-31` };
    case 'this_month': return { from: ymd(new Date(y, m, 1)), to: ymd(new Date(y, m + 1, 0)) };
    case 'last_month': return { from: ymd(new Date(y, m - 1, 1)), to: ymd(new Date(y, m, 0)) };
    case 'this_quarter': { const q = Math.floor(m / 3) * 3; return { from: ymd(new Date(y, q, 1)), to: ymd(new Date(y, q + 3, 0)) }; }
    case 'last_year': return { from: `${y - 1}-01-01`, to: `${y - 1}-12-31` };
    case 'all': return { from: '2000-01-01', to: ymd(now) };
    default: return { from: `${y}-01-01`, to: ymd(now) }; // this year to date
  }
}

// [marker: cash-basis v1] Cash / Accrual switch, remembered per computer (cash is NJJBA's reporting basis)
const BASIS_KEY = 'books:basis';
export function useBasis() {
  const [basis, setBasisState] = useState(() => { try { return localStorage.getItem(BASIS_KEY) || 'cash'; } catch { return 'cash'; } });
  const setBasis = (b) => { setBasisState(b); try { localStorage.setItem(BASIS_KEY, b); } catch {} };
  return [basis, setBasis];
}
export function BasisToggle({ value, onChange }) {
  return (
    <span style={{ display: 'inline-flex', border: '1px solid var(--gray-300)', borderRadius: 6, overflow: 'hidden' }} title="Cash: income when the money comes in, expenses when paid. Accrual: when invoiced / billed.">
      {['cash', 'accrual'].map(b => (
        <button key={b} type="button" onClick={() => onChange(b)}
                style={{ border: 'none', padding: '6px 12px', fontSize: '0.82rem', cursor: 'pointer', fontWeight: 600,
                         background: value === b ? 'var(--navy)' : 'white', color: value === b ? 'white' : 'var(--gray-700)' }}>
          {b === 'cash' ? 'Cash basis' : 'Accrual basis'}
        </button>
      ))}
    </span>
  );
}

export function RangePicker({ value, onChange, single }) {
  const fsm = useFiscalStart();
  if (single) {
    return (
      <span style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
        <span style={{ fontSize: '0.85rem', color: 'var(--gray-600)' }}>As of</span>
        <input type="date" className="form-input" style={{ height: 34, width: 160 }} value={value} onChange={e => onChange(e.target.value)} />
        <button className="btn btn-ghost btn-sm" onClick={() => onChange(ymd(new Date()))}>Today</button>
        <button className="btn btn-ghost btn-sm" onClick={() => onChange(`${new Date().getFullYear() - 1}-12-31`)}>End of last year</button>
      </span>
    );
  }
  return (
    <span style={{ display: 'inline-flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
      <select className="form-select" style={{ width: 'auto', height: 34 }} value="" onChange={e => e.target.value && onChange(rangePreset(e.target.value))}>
        <option value="">Dates…</option>
        <option value="this_month">This month</option>
        <option value="last_month">Last month</option>
        <option value="this_quarter">This quarter</option>
        <option value="ytd">This year to date</option>
        <option value="this_year">This calendar year</option>
        <option value="last_year">Last calendar year</option>
        {fsm !== 1 && <option value="fy_to_date">This fiscal year to date</option>}
        {fsm !== 1 && <option value="this_fy">This fiscal year</option>}
        {fsm !== 1 && <option value="last_fy">Last fiscal year</option>}
        <option value="all">All dates</option>
      </select>
      <input type="date" className="form-input" style={{ height: 34, width: 155 }} value={value.from} onChange={e => onChange({ ...value, from: e.target.value })} />
      <span>–</span>
      <input type="date" className="form-input" style={{ height: 34, width: 155 }} value={value.to} onChange={e => onChange({ ...value, to: e.target.value })} />
    </span>
  );
}

// Tree with roll-ups: each account shows its own amount plus everything under it
function rollup(accounts) {
  const ordered = orderAccounts(accounts.map(a => ({ ...a, sort_order: a.sort_order || 0 })));
  const byId = new Map(ordered.map(a => [a.id, { ...a, total: parseFloat(a.amount) || 0, kids: [] }]));
  ordered.forEach(a => { if (a.parent_account_id && byId.has(a.parent_account_id)) byId.get(a.parent_account_id).kids.push(a.id); });
  const calc = (id) => { const n = byId.get(id); n.total = n.kids.reduce((s, k) => s + calc(k), parseFloat(n.amount) || 0); return n.total; };
  ordered.filter(a => !a.parent_account_id || !byId.has(a.parent_account_id)).forEach(a => calc(a.id));
  return ordered.map(a => byId.get(a.id));
}

function Section({ title, rows, total, hideZero, onDrill, totalLabel }) {
  const shown = rows.filter(r => !hideZero || Math.abs(r.total) > 0.004);
  if (hideZero && !shown.length && Math.abs(total || 0) < 0.005) return null;
  return (
    <>
      <tr><td colSpan={2} style={{ paddingTop: 14, fontWeight: 700, fontSize: '0.95rem', color: 'var(--navy)' }}>{title}</td></tr>
      {shown.length === 0 && <tr><td colSpan={2} style={{ color: 'var(--gray-400)', paddingLeft: 16, fontSize: '0.85rem' }}>None</td></tr>}
      {shown.map(r => (
        <tr key={r.id} className="rpt-row" onClick={() => onDrill && onDrill(r)} style={{ cursor: onDrill ? 'pointer' : 'default' }}>
          <td style={{ paddingLeft: 16 + r.depth * 18, fontWeight: r.kids.length ? 600 : 400, fontSize: '0.875rem', padding: '3px 8px 3px ' + (16 + r.depth * 18) + 'px' }}>
            {r.account_number ? <span style={{ color: 'var(--gray-400)', marginRight: 6 }}>{r.account_number}</span> : null}{r.name}
            {r.kids.length && Math.abs(r.total - (parseFloat(r.amount) || 0)) > 0.004 && Math.abs(parseFloat(r.amount) || 0) > 0.004
              ? <span style={{ color: 'var(--gray-400)', fontWeight: 400, fontSize: '0.75rem' }}> (incl. {money(r.amount)} directly)</span> : null}
          </td>
          <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums', fontWeight: r.kids.length ? 600 : 400, fontSize: '0.875rem', padding: '3px 8px' }}>{money(r.total)}</td>
        </tr>
      ))}
      <tr style={{ borderTop: '1px solid var(--gray-300)' }}>
        <td style={{ fontWeight: 700, padding: '5px 8px' }}>{totalLabel || `Total ${title}`}</td>
        <td style={{ textAlign: 'right', fontWeight: 700, padding: '5px 8px', fontVariantNumeric: 'tabular-nums' }}>{money(total)}</td>
      </tr>
    </>
  );
}

const Summary = ({ label, value, strong }) => (
  <tr style={{ background: strong ? 'var(--gray-50)' : undefined, borderTop: '2px solid var(--gray-300)' }}>
    <td style={{ fontWeight: 700, padding: '7px 8px', fontSize: strong ? '1rem' : '0.9rem' }}>{label}</td>
    <td style={{ textAlign: 'right', fontWeight: 700, padding: '7px 8px', fontSize: strong ? '1rem' : '0.9rem', color: value < 0 ? 'var(--danger)' : undefined, fontVariantNumeric: 'tabular-nums' }}>{money(value)}</td>
  </tr>
);

const ReportShell = ({ title, subtitle, controls, children }) => (
  <div>
    <div className="no-print" style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', marginBottom: 14 }}>
      {controls}
      <button className="btn btn-secondary btn-sm" style={{ marginLeft: 'auto' }} onClick={() => window.print()}>🖨 Print</button>
    </div>
    <div className="card" style={{ maxWidth: 820, margin: '0 auto', padding: '22px 28px' }}>
      <div style={{ textAlign: 'center', marginBottom: 12 }}>
        <div style={{ fontWeight: 700, fontSize: '1.15rem' }}>{title}</div>
        <div style={{ color: 'var(--gray-500)', fontSize: '0.85rem' }}>{subtitle}</div>
      </div>
      {children}
    </div>
  </div>
);

// ─── Profit & Loss ────────────────────────────────────────────────────────────
export function ProfitLoss({ onDrill }) {
  const [range, setRange] = useState(rangePreset('ytd'));
  const [hideZero, setHideZero] = useState(true);
  const [data, setData] = useState(null);
  const [cols, setCols] = useState('total'); // [marker: fin-reports v1]
  const [basis, setBasis] = useBasis();
  useEffect(() => {
    // open on the fiscal year when the fiscal year isn't January
    loadFiscalStart().then(m => { if (m !== 1) setRange(rangePreset('fy_to_date')); });
  }, []);
  useEffect(() => {
    if (cols !== 'total') return;
    setData(null);
    api.get(`/account-reports/pl?from=${range.from}&to=${range.to}&basis=${basis}`).then(r => setData(r.data)).catch(() => toast.error('Could not load the report.'));
  }, [range, cols, basis]);
  const colPicker = (
    <select className="form-select" style={{ width: 'auto', height: 34 }} value={cols} onChange={e => setCols(e.target.value)}>
      <option value="total">Total only</option>
      <option value="month">Columns: by month</option>
      <option value="quarter">Columns: by quarter</option>
      <option value="prev_year">Compare to previous year</option>
    </select>
  );
  const tree = useMemo(() => (data ? rollup(data.accounts) : []), [data]);
  const of = (t) => tree.filter(a => a.account_type === t);
  const t = data?.totals;
  if (cols !== 'total') return <ProfitLossColumns range={range} setRange={setRange} cols={cols} colPicker={colPicker} hideZero={hideZero} setHideZero={setHideZero} onDrill={onDrill} basis={basis} basisToggle={<BasisToggle value={basis} onChange={setBasis} />} />;
  return (
    <ReportShell title="Profit & Loss" subtitle={`${fmtDay(range.from)} – ${fmtDay(range.to)} · ${basis === 'cash' ? 'Cash basis' : 'Accrual basis'}`}
      controls={<>
        <BasisToggle value={basis} onChange={setBasis} />
        <RangePicker value={range} onChange={setRange} />
        {colPicker}
        <label style={{ fontSize: '0.85rem', display: 'inline-flex', gap: 6, alignItems: 'center' }}><input type="checkbox" checked={hideZero} onChange={e => setHideZero(e.target.checked)} /> Hide $0 accounts</label>
      </>}>
      <BooksStartNote from={range.from} />
      {!data ? <div className="spinner" /> : (
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <tbody>
            <Section title="Income" rows={of('income')} total={t.income} hideZero={hideZero} onDrill={a => onDrill(a, range)} />
            {(t.cogs || of('cost_of_goods_sold').length > 0) && <Section title="Cost of Goods Sold" rows={of('cost_of_goods_sold')} total={t.cogs} hideZero={hideZero} onDrill={a => onDrill(a, range)} />}
            {t.cogs ? <Summary label="Gross Profit" value={t.grossProfit} /> : null}
            <Section title="Expenses" rows={of('expense')} total={t.expense} hideZero={hideZero} onDrill={a => onDrill(a, range)} />
            <Summary label="Net Ordinary Income" value={t.netOrdinary} />
            {(t.otherIncome || t.otherExpense) ? <>
              <Section title="Other Income" rows={of('other_income')} total={t.otherIncome} hideZero={hideZero} onDrill={a => onDrill(a, range)} />
              <Section title="Other Expense" rows={of('other_expense')} total={t.otherExpense} hideZero={hideZero} onDrill={a => onDrill(a, range)} />
            </> : null}
            <Summary label="Net Income" value={t.netIncome} strong />
          </tbody>
        </table>
      )}
      <p className="form-hint no-print" style={{ marginTop: 10 }}>Click any account to see the transactions behind it.</p>
    </ReportShell>
  );
}

// [marker: fixes-oct21 v1] explain empty months before the books start date
let _booksStart = null; let _bsPromise = null;
function useBooksStart() {
  const [d, setD] = useState(_booksStart);
  useEffect(() => {
    if (!_bsPromise) _bsPromise = api.get('/receivables/summary').then(r => { _booksStart = r.data?.booksStart || null; return _booksStart; }).catch(() => null);
    let on = true; _bsPromise.then(v => on && setD(v)); return () => { on = false; };
  }, []);
  return d;
}
export function BooksStartNote({ from }) {
  const start = useBooksStart();
  if (!start || !from || from >= start) return null;
  return (
    <div className="no-print" style={{ fontSize: '0.82rem', background: '#eff6ff', border: '1px solid #bfdbfe', color: '#1e40af', borderRadius: 6, padding: '8px 10px', marginBottom: 10 }}>
      ℹ️ The app’s books start on <strong>{fmtDay(start)}</strong>. Amounts before that date come in with the QuickBooks history import, so earlier
      months show little or nothing here for now. For invoices and payments before then, use Sales by Item, Open Invoices, or Reports → Payments (by date).
    </div>
  );
}

// ─── P&L with columns (months / quarters / vs. previous year) ── [marker: fin-reports v1]
function rollupCols(accounts, n) {
  const ordered = orderAccounts(accounts.map(a => ({ ...a, sort_order: a.sort_order || 0 })));
  const byId = new Map(ordered.map(a => [a.id, { ...a, totals: a.amounts.slice(), kids: [] }]));
  ordered.forEach(a => { if (a.parent_account_id && byId.has(a.parent_account_id)) byId.get(a.parent_account_id).kids.push(a.id); });
  const calc = (id) => {
    const node = byId.get(id);
    node.totals = node.kids.reduce((acc, k) => { const t = calc(k); return acc.map((v, i) => v + t[i]); }, node.amounts.slice());
    return node.totals;
  };
  ordered.filter(a => !a.parent_account_id || !byId.has(a.parent_account_id)).forEach(a => calc(a.id));
  return ordered.map(a => byId.get(a.id));
}

function ProfitLossColumns({ range, setRange, cols, colPicker, hideZero, setHideZero, onDrill, basis, basisToggle }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    setData(null);
    api.get(`/fin-reports/pl-columns?from=${range.from}&to=${range.to}&cols=${cols}&basis=${basis || 'accrual'}`).then(r => setData(r.data))
      .catch(err => toast.error(err.response?.data?.error || 'Could not load the report.'));
  }, [range, cols, basis]);
  const n = data ? data.columns.length : 0;
  const tree = useMemo(() => (data ? rollupCols(data.accounts, n) : []), [data, n]);
  const compare = cols === 'prev_year';
  const showTotal = !compare && n > 1;
  const sumTop = (types) => {
    const tops = tree.filter(a => types.includes(a.account_type) && (!a.parent_account_id || !tree.some(x => x.id === a.parent_account_id)));
    return Array.from({ length: n }, (_, i) => tops.reduce((s, a) => s + a.totals[i], 0));
  };
  const minus = (a, b) => a.map((v, i) => v - b[i]);
  const plus = (a, b) => a.map((v, i) => v + b[i]);
  const cells = (vals, strong) => {
    const out = vals.map((v, i) => <td key={i} style={numStyle(strong, v)}>{money(v)}</td>);
    if (showTotal) { const t = vals.reduce((s, v) => s + v, 0); out.push(<td key="t" style={{ ...numStyle(true, t), background: 'var(--gray-50)' }}>{money(t)}</td>); }
    if (compare) {
      const d = vals[0] - vals[1];
      out.push(<td key="d" style={numStyle(strong, d)}>{money(d)}</td>);
      out.push(<td key="p" style={numStyle(strong)}>{Math.abs(vals[1]) > 0.004 ? `${((d / Math.abs(vals[1])) * 100).toFixed(1)}%` : '—'}</td>);
    }
    return out;
  };
  const width = 1 + n + (showTotal ? 1 : 0) + (compare ? 2 : 0);
  const block = (title, types) => {
    const rows = tree.filter(a => types.includes(a.account_type)).filter(a => !hideZero || a.totals.some(v => Math.abs(v) > 0.004));
    const tot = sumTop(types);
    if (hideZero && !rows.length && !tot.some(v => Math.abs(v) > 0.004)) return null;
    return (
      <React.Fragment key={title}>
        <tr><td colSpan={width} style={{ paddingTop: 14, fontWeight: 700, color: 'var(--navy)' }}>{title}</td></tr>
        {rows.map(a => (
          <tr key={a.id} className="rpt-row" style={{ cursor: 'pointer' }} onClick={() => onDrill && onDrill(a, range)}>
            <td style={{ padding: `3px 8px 3px ${16 + a.depth * 18}px`, fontSize: '0.85rem', fontWeight: a.kids.length ? 600 : 400, whiteSpace: 'nowrap' }}>
              {a.account_number ? <span style={{ color: 'var(--gray-400)', marginRight: 6 }}>{a.account_number}</span> : null}{a.name}
            </td>
            {cells(a.totals, a.kids.length > 0)}
          </tr>
        ))}
        <tr style={{ borderTop: '1px solid var(--gray-300)' }}>
          <td style={{ fontWeight: 700, padding: '5px 8px' }}>Total {title}</td>{cells(tot, true)}
        </tr>
      </React.Fragment>
    );
  };
  let income, cogs, expense, oi, oe, ordinary, net;
  if (data) {
    income = sumTop(['income']); cogs = sumTop(['cost_of_goods_sold']); expense = sumTop(['expense']);
    oi = sumTop(['other_income']); oe = sumTop(['other_expense']);
    ordinary = minus(minus(income, cogs), expense); net = minus(plus(ordinary, oi), oe);
  }
  const summary = (label, vals, strong) => (
    <tr style={{ borderTop: '2px solid var(--gray-300)', background: strong ? 'var(--gray-50)' : undefined }}>
      <td style={{ fontWeight: 700, padding: '7px 8px' }}>{label}</td>{cells(vals, true)}
    </tr>
  );
  return (
    <div>
      <div className="no-print" style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', marginBottom: 14 }}>
        {basisToggle}
        <RangePicker value={range} onChange={setRange} />
        {colPicker}
        <label style={{ fontSize: '0.85rem', display: 'inline-flex', gap: 6, alignItems: 'center' }}><input type="checkbox" checked={hideZero} onChange={e => setHideZero(e.target.checked)} /> Hide $0 accounts</label>
        <button className="btn btn-secondary btn-sm" style={{ marginLeft: 'auto' }} onClick={() => window.print()}>🖨 Print</button>
      </div>
      <div className="card" style={{ padding: '22px 20px', overflowX: 'auto' }}>
        <div style={{ textAlign: 'center', marginBottom: 12 }}>
          <div style={{ fontWeight: 700, fontSize: '1.15rem' }}>Profit &amp; Loss{compare ? ' — Previous Year Comparison' : cols === 'month' ? ' by Month' : ' by Quarter'}</div>
          <div style={{ color: 'var(--gray-500)', fontSize: '0.85rem' }}>{fmtDay(range.from)} – {fmtDay(range.to)}</div>
        </div>
        <BooksStartNote from={range.from} />
        {!data ? <div className="spinner" /> : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '2px solid var(--gray-300)' }}>
                <th style={{ textAlign: 'left', padding: '4px 8px' }}></th>
                {data.columns.map(c => <th key={c.key} style={thStyle} title={`${fmtDay(c.from)} – ${fmtDay(c.to)}`}>{c.label}</th>)}
                {showTotal && <th style={{ ...thStyle, background: 'var(--gray-50)' }}>TOTAL</th>}
                {compare && <><th style={thStyle}>$ Change</th><th style={thStyle}>% Change</th></>}
              </tr>
            </thead>
            <tbody>
              {block('Income', ['income'])}
              {cogs.some(v => Math.abs(v) > 0.004) && block('Cost of Goods Sold', ['cost_of_goods_sold'])}
              {cogs.some(v => Math.abs(v) > 0.004) && summary('Gross Profit', minus(income, cogs))}
              {block('Expenses', ['expense'])}
              {summary('Net Ordinary Income', ordinary)}
              {(oi.some(v => Math.abs(v) > 0.004) || oe.some(v => Math.abs(v) > 0.004)) && <>
                {block('Other Income', ['other_income'])}
                {block('Other Expense', ['other_expense'])}
              </>}
              {summary('Net Income', net, true)}
            </tbody>
          </table>
        )}
      </div>
      <p className="form-hint no-print" style={{ marginTop: 10 }}>Click any account to see the transactions behind it. Hover a column title for its exact dates.</p>
    </div>
  );
}
const thStyle = { textAlign: 'right', padding: '4px 8px', fontSize: '0.75rem', textTransform: 'uppercase', color: 'var(--gray-600)', whiteSpace: 'nowrap' };
const numStyle = (strong, v) => ({ textAlign: 'right', padding: '3px 8px', fontSize: '0.85rem', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap',
  fontWeight: strong ? 700 : 400, color: v !== undefined && v < -0.004 ? 'var(--danger)' : undefined });

// ─── Balance Sheet ────────────────────────────────────────────────────────────
export function BalanceSheet({ onDrill }) {
  const [asOf, setAsOf] = useState(ymd(new Date()));
  const [hideZero, setHideZero] = useState(true);
  const [data, setData] = useState(null);
  const [basis, setBasis] = useBasis();
  useEffect(() => {
    setData(null);
    api.get(`/account-reports/balance-sheet?asOf=${asOf}&basis=${basis}`).then(r => setData(r.data)).catch(() => toast.error('Could not load the report.'));
  }, [asOf, basis]);
  const tree = useMemo(() => (data ? rollup(data.accounts) : []), [data]);
  const pick = (types) => tree.filter(a => types.includes(a.account_type));
  const t = data?.totals;
  const drill = (a) => onDrill(a, { from: '2000-01-01', to: asOf });
  const sum = (types) => pick(types).filter(a => !a.parent_account_id || !tree.some(x => x.id === a.parent_account_id)).reduce((s, a) => s + a.total, 0);
  return (
    <ReportShell title="Balance Sheet" subtitle={`As of ${fmtDay(asOf)} · ${basis === 'cash' ? 'Cash basis' : 'Accrual basis'}`}
      controls={<>
        <BasisToggle value={basis} onChange={setBasis} />
        <RangePicker single value={asOf} onChange={setAsOf} />
        <label style={{ fontSize: '0.85rem', display: 'inline-flex', gap: 6, alignItems: 'center' }}><input type="checkbox" checked={hideZero} onChange={e => setHideZero(e.target.checked)} /> Hide $0 accounts</label>
      </>}>
      {!data ? <div className="spinner" /> : (
        <>
          {Math.abs(t.difference) > 0.004 && (
            <div style={{ fontSize: '0.82rem', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 6, padding: '8px 10px', marginBottom: 10 }}>
              Out of balance by {money(t.difference)} — usually an old one-sided entry in a register. Open it there and add the missing account (or delete it).
            </div>
          )}
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <tbody>
              <tr><td colSpan={2} style={{ fontWeight: 800, fontSize: '1rem', paddingTop: 6 }}>ASSETS</td></tr>
              <Section title="Bank Accounts" rows={pick(['bank'])} total={sum(['bank'])} hideZero={hideZero} onDrill={drill} />
              <Section title="Accounts Receivable" rows={pick(['accounts_receivable'])} total={sum(['accounts_receivable'])} hideZero={hideZero} onDrill={drill} />
              <Section title="Other Current Assets" rows={pick(['asset'])} total={sum(['asset'])} hideZero={hideZero} onDrill={drill} />
              {pick(['fixed_asset', 'other_asset']).length > 0 && <Section title="Fixed & Other Assets" rows={pick(['fixed_asset', 'other_asset'])} total={sum(['fixed_asset', 'other_asset'])} hideZero={hideZero} onDrill={drill} />}
              <Summary label="TOTAL ASSETS" value={t.assets} strong />
              <tr><td colSpan={2} style={{ fontWeight: 800, fontSize: '1rem', paddingTop: 18 }}>LIABILITIES &amp; EQUITY</td></tr>
              <Section title="Accounts Payable" rows={pick(['accounts_payable'])} total={sum(['accounts_payable'])} hideZero={hideZero} onDrill={drill} />
              <Section title="Credit Cards" rows={pick(['credit_card'])} total={sum(['credit_card'])} hideZero={hideZero} onDrill={drill} />
              <Section title="Other Liabilities & Loans" rows={pick(['other_liability', 'loan'])} total={sum(['other_liability', 'loan'])} hideZero={hideZero} onDrill={drill} />
              <Summary label="Total Liabilities" value={t.liabilities} />
              <Section title="Equity" rows={pick(['equity'])} total={sum(['equity'])} hideZero={hideZero} onDrill={drill} totalLabel="Equity accounts" />
              <tr><td style={{ padding: '3px 8px 3px 16px', fontSize: '0.875rem' }}>Net Income (this year)</td><td style={{ textAlign: 'right', padding: '3px 8px', fontSize: '0.875rem' }}>{money(data.netIncomeThisYear)}</td></tr>
              <Summary label="Total Equity" value={t.equity} />
              <Summary label="TOTAL LIABILITIES & EQUITY" value={t.liabilitiesAndEquity} strong />
            </tbody>
          </table>
        </>
      )}
    </ReportShell>
  );
}

// ─── Trial Balance ────────────────────────────────────────────────────────────
export function TrialBalance({ onDrill }) {
  const [asOf, setAsOf] = useState(ymd(new Date()));
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get(`/account-reports/trial-balance?asOf=${asOf}`).then(r => setData(r.data)).catch(() => toast.error('Could not load the report.'));
  }, [asOf]);
  const rows = useMemo(() => (data ? orderAccounts(data.accounts.map(a => ({ ...a, sort_order: a.sort_order || 0 }))) : []), [data]);
  return (
    <ReportShell title="Trial Balance" subtitle={`As of ${fmtDay(asOf)}`} controls={<RangePicker single value={asOf} onChange={setAsOf} />}>
      {!data ? <div className="spinner" /> : (
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
          <thead><tr style={{ borderBottom: '2px solid var(--gray-300)' }}>
            <th style={{ textAlign: 'left', padding: 6 }}>Account</th><th style={{ textAlign: 'left', padding: 6 }}>Type</th>
            <th style={{ textAlign: 'right', padding: 6 }}>Debit</th><th style={{ textAlign: 'right', padding: 6 }}>Credit</th></tr></thead>
          <tbody>
            {rows.map(a => (
              <tr key={a.id} className="rpt-row" style={{ cursor: 'pointer' }} onClick={() => onDrill(a, { from: `${asOf.slice(0, 4)}-01-01`, to: asOf })}>
                <td style={{ padding: '3px 6px' }}>{a.fullName}</td>
                <td style={{ padding: '3px 6px', color: 'var(--gray-500)' }}>{TYPE_LABELS[a.account_type]}</td>
                <td style={{ padding: '3px 6px', textAlign: 'right' }}>{a.debit ? money(a.debit) : ''}</td>
                <td style={{ padding: '3px 6px', textAlign: 'right' }}>{a.credit ? money(a.credit) : ''}</td>
              </tr>
            ))}
            <tr style={{ borderTop: '2px solid var(--gray-300)', fontWeight: 700 }}>
              <td style={{ padding: 6 }}>TOTAL</td><td />
              <td style={{ padding: 6, textAlign: 'right' }}>{money(data.totals.debit)}</td>
              <td style={{ padding: 6, textAlign: 'right' }}>{money(data.totals.credit)}</td>
            </tr>
            {Math.abs(data.totals.difference) > 0.004 && (
              <tr><td colSpan={4} style={{ color: 'var(--danger)', padding: 6, fontSize: '0.82rem' }}>Out of balance by {money(data.totals.difference)} (an old one-sided entry).</td></tr>
            )}
          </tbody>
        </table>
      )}
    </ReportShell>
  );
}

// ─── Account report (QuickReport) ─────────────────────────────────────────────
export function AccountReport({ initialAccountId, initialRange }) {
  const { accounts } = useAccounts({ includeInactive: true });
  const [accountId, setAccountId] = useState(initialAccountId || null);
  const [range, setRange] = useState(initialRange || rangePreset('ytd'));
  const [sub, setSub] = useState(true);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  useEffect(() => { if (initialAccountId) setAccountId(initialAccountId); }, [initialAccountId]);
  useEffect(() => { if (initialRange) setRange(initialRange); }, [initialRange]);
  const load = useCallback(() => {
    if (!accountId) return;
    setLoading(true);
    api.get(`/account-reports/account/${accountId}?from=${range.from}&to=${range.to}&sub=${sub ? 1 : 0}`)
      .then(r => setData(r.data)).catch(() => toast.error('Could not load the report.')).finally(() => setLoading(false));
  }, [accountId, range, sub]);
  useEffect(() => { load(); }, [load]);

  const columns = [
    { key: 'entry_date', label: 'Date', width: 100, type: 'date', get: r => r.entry_date + 'T00:00:00' },
    { key: 'type', label: 'Type', width: 100 },
    { key: 'num', label: 'Num', width: 110 },
    { key: 'name', label: 'Name', width: 190 },
    { key: 'memo', label: 'Memo', width: 230 },
    ...(data?.includesSubAccounts ? [{ key: 'account_name', label: 'Account', width: 170 }] : []),
    { key: 'split', label: 'Split', width: 170 },
    { key: 'debit', label: 'Debit', width: 110, type: 'money', align: 'right', get: r => (parseFloat(r.debit) ? r.debit : null) },
    { key: 'credit', label: 'Credit', width: 110, type: 'money', align: 'right', get: r => (parseFloat(r.credit) ? r.credit : null) },
    { key: 'balance', label: 'Balance', width: 120, type: 'money', align: 'right' },
  ];
  return (
    <div>
      <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', marginBottom: 12 }}>
        <div style={{ minWidth: 300 }}>
          <AccountSelect accounts={accounts} value={accountId} onChange={setAccountId} placeholder="— Choose an account —" />
        </div>
        <RangePicker value={range} onChange={setRange} />
        <label style={{ fontSize: '0.85rem', display: 'inline-flex', gap: 6, alignItems: 'center' }}><input type="checkbox" checked={sub} onChange={e => setSub(e.target.checked)} /> Include sub-accounts</label>
      </div>
      {data && (
        <div className="stat-grid" style={{ marginBottom: 12 }}>
          {!data.isPL && <div className="stat-card"><div className="stat-label">Balance on {fmtDay(range.from)}</div><div className="stat-value">{money(data.openingBalance)}</div></div>}
          <div className="stat-card"><div className="stat-label">{data.isPL ? 'Total for the period' : 'Change in period'}</div><div className="stat-value">{money(data.netChange)}</div></div>
          {!data.isPL && <div className="stat-card"><div className="stat-label">Balance on {fmtDay(range.to)}</div><div className="stat-value">{money(data.endingBalance)}</div></div>}
          <div className="stat-card"><div className="stat-label">Type</div><div className="stat-value" style={{ fontSize: '1rem' }}>{TYPE_LABELS[data.account.account_type]}</div></div>
        </div>
      )}
      {!accountId ? <div className="empty-state"><h3>Choose an account</h3><p>Pick any account to see every transaction in it.</p></div> : (
        <DataGrid gridKey="account-report" rows={data?.rows || []} rowKey="line_id" loading={loading} columns={columns}
                  totals={['debit', 'credit']} persistFilters={false} height="calc(100vh - 400px)" emptyText="No transactions in this period." />
      )}
    </div>
  );
}
